import React from 'react';
import { Link } from 'react-router-dom';
import Reveal from '../components/Reveal';
import { ArrowRight, Check, ServiceIcon } from '../components/Icons';
import usePageTitle from '../hooks/usePageTitle';
import { SERVICES } from '../data/content';
import './ServicesPage.css';

const FAQS = [
  {
    q: 'Do you only work with neurodivergent clients?',
    a: 'Not at all. We work with anyone who wants software that is carefully built and usable by everyone. Our neurodiverse perspective is what we bring to the table.',
  },
  {
    q: 'How do you communicate during a project?',
    a: 'Written-first. You get a weekly written update and a demo, and decisions are documented. Calls are welcome, and every one has an agenda shared in advance.',
  },
  {
    q: 'What does "accessible by default" mean?',
    a: 'Every build targets WCAG 2.1 AA from the first commit: semantic markup, full keyboard support, screen-reader testing, sufficient colour contrast and respect for reduced-motion preferences.',
  },
  {
    q: 'Can you audit a product we already have?',
    a: 'Yes. We run a full accessibility audit, deliver a prioritised remediation roadmap, and can either fix the issues ourselves or train your team to.',
  },
];

const ServicesPage = () => {
  usePageTitle('Services');

  return (
    <div className="services-page">
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow">Services</p>
          <h1>
            Innovative solutions, <span className="highlight">unique perspective.</span>
          </h1>
          <p className="lead">
            Technology services designed around your business needs, with accessibility, usability and innovation as
            the priority.
          </p>
          <nav className="service-jump" aria-label="Jump to a service">
            {SERVICES.map((s) => (
              <a key={s.id} href={`#${s.id}`} className="tag">
                {s.title}
              </a>
            ))}
          </nav>
        </div>
      </section>

      <section className="section">
        <div className="container service-list">
          {SERVICES.map((service, i) => (
            <Reveal key={service.id} id={service.id} className="service-row">
              <div className="service-row-head">
                <span className="service-row-num">0{i + 1}</span>
                <div className="service-icon">
                  <ServiceIcon name={service.icon} />
                </div>
                <h2>{service.title}</h2>
                <p>{service.description}</p>
              </div>
              <div className="service-row-body">
                <h3>What's included</h3>
                <ul>
                  {service.details.map((detail) => (
                    <li key={detail}>
                      <span className="check">
                        <Check />
                      </span>
                      {detail}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section section-alt">
        <div className="container faq-grid">
          <Reveal>
            <p className="eyebrow">FAQ</p>
            <h2 className="section-title">Questions, answered in writing.</h2>
            <p className="lead">Can't find what you're looking for? Ask us anything.</p>
          </Reveal>
          <Reveal delay={120} className="faq-list">
            {FAQS.map((item) => (
              <details key={item.q} className="faq-item">
                <summary>
                  {item.q}
                  <span className="faq-icon" aria-hidden="true" />
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <Reveal className="services-cta">
            <h2>Ready to work with us?</h2>
            <p className="lead">Get in touch to discuss how we can help with your next project.</p>
            <Link to="/contact" className="btn">
              Contact us <ArrowRight />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
